import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { Entity } from '../lib/api';

interface EntityContextType {
  currentEntity: Entity | null;
  setCurrentEntity: (entity: Entity | null) => void;
}

const EntityContext = createContext<EntityContextType | null>(null);

const STORAGE_KEY = 'str-forms-entity-id';

export function EntityProvider({ children }: { children: React.ReactNode }) {
  const { entities } = useAuth();
  const [currentEntity, setCurrentEntityState] = useState<Entity | null>(null);

  // Load saved entity on mount
  useEffect(() => {
    const savedId = localStorage.getItem(STORAGE_KEY);
    if (savedId && entities.length > 0) {
      const saved = entities.find(e => e.id === savedId);
      if (saved) {
        setCurrentEntityState(saved);
        return;
      }
    }
    // Default to first entity
    if (entities.length > 0 && !currentEntity) {
      setCurrentEntityState(entities[0]);
    }
  }, [entities]);

  const setCurrentEntity = (entity: Entity | null) => {
    setCurrentEntityState(entity);
    if (entity) {
      localStorage.setItem(STORAGE_KEY, entity.id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  return (
    <EntityContext.Provider value={{ currentEntity, setCurrentEntity }}>
      {children}
    </EntityContext.Provider>
  );
}

export function useEntity() {
  const context = useContext(EntityContext);
  if (!context) {
    throw new Error('useEntity must be used within EntityProvider');
  }
  return context;
}
